import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, Award, Trophy, BarChart3, DoorOpen, AlertTriangle, Loader2, Bell, Mail, Calendar, TrendingUp as TrUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Community Settings tab. Renders the granular privacy toggles
 * (all default OFF, anon_benchmarks default ON) plus a "Leave the
 * Community" button.
 *
 * Every toggle is wired to /api/community/privacy PATCH. Optimistic
 * UI — toggle flips instantly, error rolls it back.
 */
const TOGGLE_DEFS = [
  {
    key: 'anon_benchmarks',
    label: 'Contribute to anonymous benchmarks',
    desc: 'Your sector weights & yields are pooled into community averages. Identity stripped, no dollars shared.',
    Icon: BarChart3,
  },
  {
    key: 'share_tickers',
    label: 'Allow me to publish public watchlists',
    desc: 'Required before you can publish any of your 3 watchlists (5 tickers max each). Share counts never published.',
    Icon: Eye,
  },
  {
    key: 'show_badges',
    label: 'Show my milestone badges on my profile',
    desc: 'Your earned badges (Coffee Payer, Utility Master, etc.) appear on your public profile. Dollar amounts never shown.',
    Icon: Award,
  },
  {
    key: 'show_health_score',
    label: 'Show my portfolio Quality Score on my profile',
    desc: 'Average Quality Score across your holdings appears on your public profile (0–100).',
    Icon: Lock,
  },
  {
    key: 'leaderboard_opt_in',
    label: 'Appear on the Diversification leaderboard',
    desc: 'Your handle and diversification score (sector + asset-type spread) appear in the public ranking. Never ranked by yield.',
    Icon: Trophy,
  },
];

const NOTIF_DEFS = [
  {
    key: 'email_ex_div_reminders',
    label: 'Ex-Dividend Date reminders',
    desc: 'Email me a few days before an ex-div date for stocks in my Verified Owner set.',
    Icon: Calendar,
  },
  {
    key: 'email_payout_increases',
    label: 'Dividend hike announcements',
    desc: 'Email me when a stock I own raises its dividend.',
    Icon: TrUp,
  },
  {
    key: 'email_milestones',
    label: 'New milestone badges',
    desc: 'Email me when I unlock a new passive-income milestone (Coffee Payer, Utility Master, etc.).',
    Icon: Award,
  },
  {
    key: 'email_watchlist_follows',
    label: 'Public watchlist activity',
    desc: 'Email me when another member follows one of my published watchlists.',
    Icon: Eye,
  },
  {
    key: 'email_weekly_digest',
    label: 'Weekly community digest',
    desc: 'A short summary every Sunday covering community sentiment, new badges, and top watchlists.',
    Icon: Mail,
  },
  {
    key: 'email_brokerage_sync_status',
    label: 'CSV import status (transactional)',
    desc: 'Email me when a CSV import finishes or fails. Opt-out style — leave on to keep the import flow trustworthy.',
    Icon: Bell,
  },
];

export default function CommunitySettingsTab({ me, onChange }) {
  const navigate = useNavigate();
  const [privacy, setPrivacy] = useState(me?.privacy || {});
  const [notif, setNotif] = useState(me?.notifications || {});
  const [saving, setSaving] = useState(null);
  const [leaving, setLeaving] = useState(false);

  const togglePref = async (key, value) => {
    const prev = privacy[key];
    setPrivacy({ ...privacy, [key]: value });
    setSaving(key);
    try {
      const { data } = await axios.patch(
        `${API}/community/privacy`,
        { [key]: value },
        { withCredentials: true },
      );
      setPrivacy(data.privacy);
      if (onChange) onChange({ ...me, privacy: data.privacy });
    } catch (e) {
      setPrivacy({ ...privacy, [key]: prev });
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally {
      setSaving(null);
    }
  };

  const toggleNotif = async (key, value) => {
    const prev = notif[key];
    setNotif({ ...notif, [key]: value });
    setSaving(`notif_${key}`);
    try {
      const { data } = await axios.patch(
        `${API}/community/notifications`,
        { [key]: value },
        { withCredentials: true },
      );
      setNotif(data.notifications);
      if (onChange) onChange({ ...me, notifications: data.notifications });
    } catch (e) {
      setNotif({ ...notif, [key]: prev });
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally {
      setSaving(null);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave the DiviDrip Community? This wipes your handle, public watchlists, badges, and opt-ins. Your tracker data is untouched.')) return;
    setLeaving(true);
    try {
      await axios.post(`${API}/community/leave`, {}, { withCredentials: true });
      toast.success('You\u2019ve left the community.', { description: 'You can rejoin any time.' });
      navigate('/');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not leave.');
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="community-settings-tab">
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <h3 className="font-bold text-foreground">Privacy &amp; Visibility</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Every toggle defaults to OFF (except anonymous benchmarking). You're in control.</p>
        </div>
        <ul className="divide-y divide-border">
          {TOGGLE_DEFS.map(({ key, label, desc, Icon }) => (
            <li key={key} className="px-4 py-3 flex items-start gap-3" data-testid={`community-toggle-${key}`}>
              <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={!!privacy[key]}
                  onChange={(e) => togglePref(key, e.target.checked)}
                  disabled={saving === key}
                  className="sr-only peer"
                  data-testid={`community-toggle-input-${key}`}
                />
                {/* Round 84k Phase 4 — toggle visibility fix.
                    OFF state in BOTH themes uses a visible mid-gray
                    track + white knob, so the switch reads clearly
                    on either background. ON state stays violet. */}
                <div className="pointer-events-none w-10 h-6 bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-violet-500 dark:peer-checked:bg-violet-400 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white dark:after:bg-zinc-200 after:border after:border-zinc-300 dark:after:border-zinc-400 after:rounded-full after:h-5 after:w-5 after:shadow-sm after:transition-all peer-checked:after:translate-x-4 peer-checked:after:bg-white peer-checked:after:border-violet-200" />
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border-2 border-rose-200 dark:border-rose-900/40 bg-rose-50/50 dark:bg-rose-950/20 p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-foreground">Leave the Community</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Wipes your handle, public watchlists, badges, and opt-ins. Your tracker data (positions, journal, watchlist) is untouched. You can rejoin any time.
          </p>
          <Button
            onClick={handleLeave}
            disabled={leaving}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="community-leave-btn"
          >
            {leaving ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Leaving…</>) : (<><DoorOpen className="w-4 h-4 mr-1" /> Leave Community</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
